import { z } from "zod";
import { e164Schema, emailSchema, hexColourSchema, localeSchema, slugSchema, timestampSchema, urlSchema, hhmmSchema, } from "../common.js";
import { availabilitySchema, bookingSettingsSchema } from "./booking.js";
export const PLANS = ["starter", "clinic", "group"];
export const planSchema = z.enum(PLANS);
export const brandingSchema = z.object({
    color: hexColourSchema.default("#1F4E79"),
    logoPath: z.string().max(512).optional(),
    buttonPosition: z.enum(["right", "left"]).default("right"),
    buttonLabel: z.string().max(40).default("Chat with us"),
    buttonIcon: z.enum(["chat", "message", "help"]).default("chat"),
    /** AGENTS.md 5.4: dashboard does not expose the toggle until the owner decides. */
    showBadge: z.boolean().default(true),
});
/** Section 14b: floating button and chat-funnel behaviour, configured per clinic. */
export const DAY_KEYS = ["mon", "tue", "wed", "thu", "fri", "sat", "sun"];
export const dayKeySchema = z.enum(DAY_KEYS);
export const weeklyHoursSchema = z.partialRecord(dayKeySchema, z.array(z.object({ from: hhmmSchema, to: hhmmSchema })).max(3));
export const urlPatternSchema = z.string().min(1).max(200);
export const widgetAgentSchema = z.object({
    id: z.string().min(1).max(40),
    name: z.string().min(1).max(80),
    title: z.string().max(80).optional(),
    photoPath: z.string().max(512).optional(),
    whatsappNumber: e164Schema,
    /** Procedure categories this agent handles (routing "category"). */
    categories: z.array(z.string().max(80)).max(30).default([]),
    /** URL patterns this agent handles (routing "page"). */
    pages: z.array(urlPatternSchema).max(30).default([]),
    hours: weeklyHoursSchema.optional(),
    active: z.boolean().default(true),
});
export const widgetSettingsSchema = z.object({
    showOn: z.enum(["all", "mobile", "desktop"]).default("all"),
    delayMs: z.number().int().min(0).max(60_000).default(0),
    scrollPercent: z.number().int().min(0).max(100).default(0),
    offset: z
        .object({
        x: z.number().int().min(0).max(200).default(20),
        y: z.number().int().min(0).max(200).default(20),
    })
        .default({ x: 20, y: 20 }),
    animation: z.enum(["ring", "none"]).default("ring"),
    cta: z
        .object({
        enabled: z.boolean().default(false),
        text: z.string().max(160).default("Hello, how can we help?"),
        imagePath: z.string().max(512).optional(),
        delayMs: z.number().int().min(0).max(60_000).default(3000),
        showOncePerVisit: z.boolean().default(true),
        dismissible: z.boolean().default(true),
    })
        .default({
        enabled: false,
        text: "Hello, how can we help?",
        delayMs: 3000,
        showOncePerVisit: true,
        dismissible: true,
    }),
    pages: z
        .object({
        include: z.array(urlPatternSchema).max(50).default([]),
        exclude: z.array(urlPatternSchema).max(50).default([]),
    })
        .default({ include: [], exclude: [] }),
    hours: z
        .object({
        enabled: z.boolean().default(false),
        weekly: weeklyHoursSchema.default({}),
        /** YYYY-MM-DD in the clinic time zone. */
        holidays: z
            .array(z.string().regex(/^\d{4}-\d{2}-\d{2}$/))
            .max(60)
            .default([]),
        awayMessage: z
            .string()
            .max(200)
            .default("We are away right now. Leave your details and we will reply as soon as we are back."),
    })
        .default({
        enabled: false,
        weekly: {},
        holidays: [],
        awayMessage: "We are away right now. Leave your details and we will reply as soon as we are back.",
    }),
    agents: z
        .object({
        routing: z.enum(["off", "category", "page", "round_robin", "choose"]).default("off"),
        list: z.array(widgetAgentSchema).max(20).default([]),
    })
        .default({ routing: "off", list: [] }),
    countries: z
        .object({
        mode: z.enum(["off", "allow", "block"]).default("off"),
        /** ISO 3166-1 alpha-2, upper case. */
        list: z
            .array(z.string().regex(/^[A-Z]{2}$/))
            .max(100)
            .default([]),
    })
        .default({ mode: "off", list: [] }),
    qrOnDesktop: z.boolean().default(false),
    interceptWaLinks: z.boolean().default(false),
    /** Per-page overrides (14b): the first matching pattern wins. */
    pageOverrides: z
        .array(z.object({
        pattern: urlPatternSchema,
        buttonLabel: z.string().max(40).optional(),
        ctaText: z.string().max(160).optional(),
    }))
        .max(50)
        .default([]),
    tracking: z
        .object({
        dataLayer: z.boolean().default(false),
        ga4: z.boolean().default(false),
        metaPixel: z.boolean().default(false),
        googleAds: z
            .object({
            conversionId: z.string().max(40).optional(),
            label: z.string().max(80).optional(),
            /**
             * Which moments count as a Google Ads conversion (owner request, 14 October 2026).
             * Missing = chat completed only, as before.
             */
            countOn: z
                .array(z.enum(["lead_completed", "whatsapp_open"]))
                .max(2)
                .optional(),
        })
            .default({}),
    })
        .default({ dataLayer: false, ga4: false, metaPixel: false, googleAds: {} }),
});
export const DEFAULT_WIDGET_SETTINGS = widgetSettingsSchema.parse({});
export const bookingProviderSchema = z.discriminatedUnion("type", [
    z.object({ type: z.literal("none") }),
    z.object({
        type: z.literal("external"),
        externalUrl: urlSchema,
        externalLabel: z.string().max(40).default("Book an appointment"),
    }),
    z.object({ type: z.literal("internal") }), // Phase 2
]);
export const replyTemplateSchema = z.object({
    name: z.string().min(1).max(80),
    text: z.string().min(1).max(1000),
    isDefault: z.boolean().default(false),
    order: z.number().int().default(0),
});
export const licenceSchema = z.object({
    /** `manual`: paid outside Stripe (bank transfer), entered from the admin app. */
    source: z.enum(["stripe", "comp", "trial", "manual"]),
    plan: planSchema,
    startsAt: timestampSchema,
    endsAt: timestampSchema.optional(),
    grantedBy: z.string().optional(),
    note: z.string().max(500).optional(),
    /** Websites this licence covers (section 13): the Stripe quantity, or set on a complimentary grant. Missing = 1. */
    sites: z.number().int().min(1).max(100).optional(),
});
export const clinicSchema = z.object({
    name: z.string().min(1).max(120),
    slug: slugSchema,
    timezone: z.string().min(1).max(64).default("Europe/London"),
    defaultLocale: localeSchema.default("en-GB"),
    domains: z.array(z.string().min(3).max(253)).max(20).default([]),
    whatsappNumber: e164Schema,
    branding: brandingSchema,
    widget: widgetSettingsSchema.default(DEFAULT_WIDGET_SETTINGS),
    acquisition: z
        .object({
        referredByClinic: z.string().optional(),
        utm: z.record(z.string(), z.string()).optional(),
    })
        .default({}),
    bookingProvider: bookingProviderSchema.default({ type: "none" }),
    booking: bookingSettingsSchema.optional(),
    /** Postal address shown in in-person booking confirmations (section 11). */
    address: z.string().max(300).optional(),
    replyTemplates: z.array(replyTemplateSchema).max(20).default([]),
    notifications: z
        .object({
        emails: z.array(emailSchema).max(10).default([]),
        abandonedAfterMinutes: z.number().int().min(5).max(1440).default(30),
    })
        .default({ emails: [], abandonedAfterMinutes: 30 }),
    billing: z
        .object({
        stripeCustomerId: z.string().optional(),
        stripeSubscriptionId: z.string().optional(),
        subscriptionStatus: z.string().optional(),
        plan: planSchema.optional(),
        currentPeriodEnd: timestampSchema.optional(),
        cancelAt: timestampSchema.nullable().optional(),
        firstPaidAt: timestampSchema.optional(),
        paymentFailedAt: timestampSchema.nullable().optional(),
        paymentFailedAttempts: z.number().int().optional(),
        /** Extra billing contacts besides the owner (section 10 screen 12). */
        contacts: z.array(emailSchema).max(10).optional(),
        /** One-off billing emails already sent, keyed by template. */
        notified: z.record(z.string(), z.boolean()).optional(),
    })
        .default({}),
    licence: licenceSchema.optional(),
    /** Public contact email: the widget offers "send by email" as an alternative to WhatsApp when set. */
    contactEmail: emailSchema.optional(),
    /** Terms and DPA acceptance recorded at sign-up. */
    legal: z
        .object({
        termsVersion: z.string(),
        dpaVersion: z.string(),
        acceptedAt: timestampSchema,
        acceptedBy: z.string(),
        acceptedEmail: z.string().optional(),
    })
        .optional(),
    leadEmail: z
        .object({
        enabled: z.boolean().default(true),
        recipients: z.array(emailSchema).max(10).default([]),
        onCompleted: z.boolean().default(true),
        onAbandoned: z.boolean().default(true),
        onBooked: z.boolean().default(true),
        digest: z.enum(["instant", "daily"]).default("instant"),
        /** Owner request (14 October 2026): a direct wa.me link in lead emails. Puts the customer's phone number in the email, so off by default (section 9.5). */
        directWhatsApp: z.boolean().default(false),
    })
        .default({
        enabled: true,
        recipients: [],
        onCompleted: true,
        onAbandoned: true,
        onBooked: true,
        digest: "instant",
        directWhatsApp: false,
    }),
    /** D22: unanswered leads are reminded and escalated. Defaults from section 20 Q13 (proposed). */
    attention: z
        .object({
        unansweredAfterHours: z.number().min(0.5).max(72).default(2),
        escalateAfterHours: z.number().min(1).max(168).default(24),
        escalateTo: z.array(emailSchema).max(10).default([]),
    })
        .default({ unansweredAfterHours: 2, escalateAfterHours: 24, escalateTo: [] }),
    support: z.object({ allowAccess: z.boolean().default(true) }).default({ allowAccess: true }),
    analytics: z
        .object({ journeyBeforeConsent: z.boolean().default(true) })
        .default({ journeyBeforeConsent: true }),
    retention: z
        .object({
        completedMonths: z.number().int().min(1).max(120).default(24),
        abandonedMonths: z.number().int().min(1).max(120).default(6),
    })
        .default({ completedMonths: 24, abandonedMonths: 6 }),
    /** Google Calendar connection (11.4): public part only; the refresh token lives in a server-only doc. */
    google: z
        .object({
        connected: z.boolean().default(false),
        email: z.string().max(254).optional(),
        connectedAt: timestampSchema.optional(),
        connectedBy: z.string().optional(),
        /** Calendars the account can write to, cached at connection time. */
        calendars: z
            .array(z.object({
            id: z.string().max(300),
            summary: z.string().max(200),
            primary: z.boolean().optional(),
        }))
            .max(50)
            .optional(),
    })
        .optional(),
    /**
     * GoHighLevel (owner request, 14 October 2026): every lead is pushed into the business's GHL
     * sub-account as a contact (with a note and, optionally, an opportunity). The Private
     * Integration token lives in a server-only doc; this is the public part.
     */
    ghl: z
        .object({
        connected: z.boolean().default(false),
        locationId: z.string().max(64).optional(),
        locationName: z.string().max(200).optional(),
        connectedAt: timestampSchema.optional(),
        connectedBy: z.string().optional(),
        /** Tag put on every synced contact. */
        tag: z.string().max(60).default("wapadesk"),
        /** Add the answers as a note on the contact (the business is the controller; off = contact only). */
        sendAnswers: z.boolean().default(true),
        /** Create an opportunity in this pipeline and stage for every new lead. */
        pipelineId: z.string().max(64).optional(),
        pipelineStageId: z.string().max(64).optional(),
        lastSyncAt: timestampSchema.optional(),
        lastError: z.string().max(300).optional(),
    })
        .optional(),
    /**
     * Contact forms on the clinic's website (owner request, 15 October 2026): the WordPress plugin
     * sends each submission with a connection key. Server-written; the key itself is never stored,
     * only its hash in the server-only `connectionKeys` collection.
     */
    formCapture: z
        .object({
        /** First characters of the secret, so staff can tell which key a site uses. */
        keyHint: z.string().max(12).optional(),
        keyCreatedAt: timestampSchema.optional(),
        keyCreatedBy: z.string().optional(),
        lastReceivedAt: timestampSchema.optional(),
        lastSite: z.string().max(253).optional(),
        lastForm: z.string().max(120).optional(),
        /** The plugin's report of the forms it found and which ones send (titles only). */
        forms: z
            .array(z.object({
            plugin: z.string().max(40),
            id: z.string().max(64),
            title: z.string().max(120),
            enabled: z.boolean(),
            hasConsent: z.boolean().optional(),
        }))
            .max(100)
            .optional(),
        formsReportedAt: timestampSchema.optional(),
    })
        .optional(),
    /** Stripe Connect (11.3): the business's own Stripe account; direct charges, no platform fee. */
    connect: z
        .object({
        stripeAccountId: z.string().optional(),
        chargesEnabled: z.boolean().optional(),
        detailsSubmitted: z.boolean().optional(),
        updatedAt: timestampSchema.optional(),
    })
        .optional(),
    createdAt: timestampSchema,
    updatedAt: timestampSchema,
});
export const MEMBER_ROLES = ["owner", "admin", "pa", "doctor"];
export const memberRoleSchema = z.enum(MEMBER_ROLES);
export const memberSchema = z.object({
    role: memberRoleSchema,
    email: emailSchema,
    displayName: z.string().max(120).optional(),
    doctorId: z.string().optional(),
    invitedBy: z.string().optional(),
    createdAt: timestampSchema,
});
/**
 * Web Push subscription of one browser/device of a member, stored at
 * `members/{uid}/pushSubscriptions/{id}` (section 2b, reminders without the API).
 * Written only by callables; never readable from the client. Carries no patient data.
 */
export const pushSubscriptionSchema = z.object({
    endpoint: z.string().url().max(2048),
    keys: z.object({ p256dh: z.string().min(1).max(256), auth: z.string().min(1).max(256) }),
    userAgent: z.string().max(300).optional(),
    createdAt: timestampSchema,
    lastUsedAt: timestampSchema.optional(),
    failures: z.number().int().min(0).default(0),
});
export const doctorSchema = z.object({
    name: z.string().min(1).max(120),
    title: z.string().max(120).optional(),
    photoPath: z.string().max(512).optional(),
    active: z.boolean().default(true),
    order: z.number().int().default(0),
    /** Working hours for internal booking (11.4); missing = not bookable. */
    availability: availabilitySchema.optional(),
    /** Email for the doctor's own appointment notices (optional). */
    email: z.string().max(254).optional(),
    /** Google calendar (id from `clinic.google.calendars`) used for busy times and bookings (11.4). */
    googleCalendarId: z.string().max(300).optional(),
    /** Locations this specialist works at; empty = every location. */
    locationIds: z.array(z.string()).max(50).default([]),
    /** Hours at a given location when they differ from `availability` (key = location id). */
    availabilityByLocation: z.record(z.string(), availabilitySchema).optional(),
    /** How online appointments get a meeting link. */
    meetingProvider: z.enum(["none", "google_meet", "zoom"]).optional(),
    /**
     * The specialist's own Zoom meeting room (personal meeting link) when `meetingProvider` is
     * `zoom`: attached to every online booking. No Zoom API is involved (owner decision, 13 October
     * 2026: a Marketplace OAuth app would need Zoom's review before other accounts could use it).
     */
    meetingUrl: z.string().url().max(2048).optional(),
});
export const procedureSchema = z.object({
    name: z.string().min(1).max(120),
    category: z.string().min(1).max(80),
    active: z.boolean().default(true),
    order: z.number().int().default(0),
    defaultDoctorId: z.string().optional(),
    whatsappNumber: e164Schema.optional(),
    /**
     * Locations that offer it (owner request, 15 October 2026); missing or empty = every location.
     * The booking page only lists a procedure at the locations that offer it.
     */
    locationIds: z.array(z.string().max(64)).max(50).optional(),
});
/** Lead stages (10.2). The seven system keys cannot be deleted. */
export const SYSTEM_STAGE_KEYS = [
    "new",
    "contacted",
    "follow_up",
    "booked",
    "registered",
    "treated",
    "closed",
];
export const leadStageSchema = z.object({
    key: z
        .string()
        .min(1)
        .max(40)
        .regex(/^[a-z0-9_]+$/),
    label: z.string().min(1).max(40),
    colour: hexColourSchema,
    order: z.number().int(),
    isDefault: z.boolean().default(false),
    system: z.boolean().default(false),
});
/** Default stage set seeded per clinic. Colours meet WCAG AA on white and on dark surfaces. */
export const DEFAULT_LEAD_STAGES = [
    { key: "new", label: "New", colour: "#B3261E", order: 0, isDefault: true, system: true },
    {
        key: "contacted",
        label: "Contacted",
        colour: "#9A5B00",
        order: 1,
        isDefault: false,
        system: true,
    },
    {
        key: "follow_up",
        label: "Follow-up",
        colour: "#6B3FA0",
        order: 2,
        isDefault: false,
        system: true,
    },
    { key: "booked", label: "Booked", colour: "#1F4E79", order: 3, isDefault: false, system: true },
    {
        key: "registered",
        label: "Registered",
        colour: "#006D6F",
        order: 4,
        isDefault: false,
        system: true,
    },
    { key: "treated", label: "Treated", colour: "#2E6B2E", order: 5, isDefault: false, system: true },
    { key: "closed", label: "Closed", colour: "#5F6368", order: 6, isDefault: false, system: true },
];
export const messageTemplateSchema = z.object({
    name: z.string().min(1).max(80),
    greeting: z.string().max(500).default("Hello, I'm contacting you from your website."),
    closing: z.string().max(500).default(""),
    /** Node ids in output order; nodes absent here follow in form order. */
    order: z.array(z.string()).default([]),
    labels: z.record(z.string(), z.string().max(60)).default({}),
    /** Off by default (owner decision, 13 October 2026): the link makes WhatsApp show a Wapadesk preview card. */
    includeRecordLink: z.boolean().default(false),
    /** "From page: <url>" line so the team knows which page the customer wrote from (14 October 2026). */
    includePage: z.boolean().default(true),
    isDefault: z.boolean().default(false),
});
/** Outgoing webhooks (14b, Premium Plus equivalent). Stored under `clinics/{id}/webhooks`; the secret is shown once and kept for signing. */
export const WEBHOOK_EVENTS = [
    "lead.created",
    "lead.completed",
    "lead.abandoned",
    "lead.whatsapp_opened",
    "lead.booking_started",
    "lead.booked",
    "lead.stage_changed",
];
export const webhookEventSchema = z.enum(WEBHOOK_EVENTS);
export const webhookSchema = z.object({
    url: z.string().url().max(2048).startsWith("https://"),
    events: z.array(webhookEventSchema).min(1),
    secret: z.string().length(64),
    active: z.boolean().default(true),
    description: z.string().max(120).optional(),
    createdBy: z.string(),
    createdAt: timestampSchema,
    lastDeliveryAt: timestampSchema.optional(),
    lastStatus: z.number().int().optional(),
    failures: z.number().int().default(0),
});
//# sourceMappingURL=clinic.js.map